import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ModalDirective } from 'ng-uikit-pro-standard';
import { Subscription } from 'rxjs';
import { RcsMessage, MessageType, RichCard, Suggestion, SuggestionType } from '../../../../../../_models/messaging/messages/rcs-messages/rcs-message.model';
import { ValidationService } from '../../../../../../_services/messaging/messages/rcs-messages/validation.service';
import { ListFolderDto } from 'src/app/_models/messaging/lists-and-contacts/list-folders/list-folder-dto';
import { ListFolderDtoAdapter } from 'src/app/_models/messaging/lists-and-contacts/list-folders/list-folder-dto.adapter';
import { ListFolderService } from 'src/app/_services/messaging/lists-and-contacts/list-folders/list-folder.service';
import { ListsService } from 'src/app/_services/messaging/lists-and-contacts/lists/lists.service';
import { ListModel } from 'src/app/_models/messaging/lists-and-contacts/lists/list-model';
import { ListModelAdapter } from 'src/app/_models/messaging/lists-and-contacts/lists/list-model.adapter';
import { ListFieldsModel } from 'src/app/_models/messaging/lists-and-contacts/lists/list-fields-model';
import { ListFieldsModelAdapter } from 'src/app/_models/messaging/lists-and-contacts/lists/list-fields-model.adapter';
import { SelectListOption } from 'src/app/_models/system/select-list-option';
import { ExecutionResultDto } from 'src/app/_models/execution-result-model';
import { ExecutionResult } from 'src/app/_models/execution-result-enum';
import { NotificationService } from 'src/app/_services/notification.service';
import { RcsMessageService } from '../../../../../../_services/messaging/messages/rcs-messages/rcs-message.service';
import { RcsMessageDtoRequest } from 'src/app/_models/messaging/messages/rcs-messages/rcs-message-dto-request';
import { RcsMessageDtoAdapter } from 'src/app/_models/messaging/messages/rcs-messages/rcs-message-dto.adapter';
import { SmsMessageService } from 'src/app/_services/messaging/messages/sms-messages/sms-message.service';
import { ClientInboxDto } from 'src/app/_models/admin/clients/client-inboxes-model';
import { ClientInboxAdapter } from 'src/app/_models/admin/clients/client-inboxes-model';
import { LocalStorageService } from 'src/app/_services/system/localStorage/localStorage.service';
import { MediaServerService } from 'src/app/_services/media-server/media-server.service';
import { S3ObjectsModel } from 'src/app/_models/media-server/S3Models/S3Objects.model';

@Component({
  selector: 'app-rcs-rich-card',
  templateUrl: './rcs-rich-card.component.html',
  styleUrls: ['./rcs-rich-card.component.scss']
})
export class RcsRichCardComponent implements OnInit, OnDestroy, OnChanges {
  @Input() message: RcsMessage | null = null;
  @Output() messageChange = new EventEmitter<RcsMessage>();
  
  richCard: RichCard = {
    title: '',
    description: '',
    orientation: 'VERTICAL',
    thumbnailAlignment: 'LEFT',
    media: {
      url: '',
      height: 'MEDIUM'
    },
    suggestions: []
  };
  
  SuggestionType = SuggestionType;
  newSuggestion: Suggestion = {
    type: SuggestionType.REPLY,
    displayText: ''
  };
  
  // Editing state for suggestions
  editingIndex: number = -1;
  editingSuggestion: Suggestion = {
    type: SuggestionType.REPLY,
    displayText: ''
  };
  editingSuggestionErrors: { [key: string]: string } = {};
  hasThumbnail: boolean = false;

  // Validation errors
  fieldErrors: { [key: string]: string } = {};
  suggestionErrors: { [key: string]: string } = {};
  fallbackTextError: string = '';
  senderError: string = '';
  selectedSenderId: number = 0;
  fallbackText: string = '';
  _clientInboxSelectListOptions: SelectListOption[] = [];

  // Wildcard functionality
  @ViewChild('insertWildcardModal', { static: true }) insertWildcardModal: ModalDirective;
  @ViewChild('mediaServerModal', { static: true }) mediaServerModal: ModalDirective;
  _formGroupInsertWildcard: UntypedFormGroup;
  _listFolderSelectListOptions: SelectListOption[] = [];
  _listsSelecListOptions: SelectListOption[] = [];
  _listFieldsSelectListOptions: SelectListOption[] = [];
  _selectedListId = 0;
  _selectedListFieldId = 0;
  _selectedListFolderId = 0;
  _wildcardTarget: 'title' | 'description' | 'fallback' = 'title';
  _selectionStart = 0;
  _showSpinner = false;
  _showMediaServer = false;
  _mediaServerTarget: 'media' | 'thumbnail' = 'media';
  _subscriptionListFolders: Subscription;
  _subscriptionLists: Subscription;
  _subscriptionListFields: Subscription;

  constructor(
    private validationService: ValidationService,
    private smsMessageService: SmsMessageService,
    private rcsMessageService: RcsMessageService,
    private rcsMessageDtoAdapter: RcsMessageDtoAdapter,
    private clientInboxDtoAdapter: ClientInboxAdapter,
    private notificationService: NotificationService,
    private listFolderService: ListFolderService,
    private listFolderDtoAdapter: ListFolderDtoAdapter,
    private listsService: ListsService,
    private listModelAdapter: ListModelAdapter,
    private listFieldsModelAdapter: ListFieldsModelAdapter,
    private localStorageService: LocalStorageService,
    private mediaServerService: MediaServerService
  ) {}

  ngOnInit(): void {
    this.initWildcardForm();
    this.populateListFoldersDropdown();
    this.subscribeToWildcardFormControls();
    this.populateSendersDropdown();

    // IMPORTANT: Do NOT use @Input message here for edit mode!
    // In edit mode, message is loaded via ngOnChanges from message-builder
    // In create mode, message will be set later via @Input
    // Therefore, do NOT copy richCard here - let ngOnChanges handle it
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['message'] && changes['message'].currentValue) {
      const message = changes['message'].currentValue as RcsMessage;
      
      if (message.richCard) {
        // Spread copies richCard (suggestions may or may not be here, depends on implementation)
        this.richCard = { ...message.richCard };
        
        if (!this.richCard.media) {
          this.richCard.media = { url: '', height: 'MEDIUM' };
        }
        this.hasThumbnail = !!this.richCard.thumbnail;
        if (this.hasThumbnail && !this.richCard.thumbnail) {
          this.richCard.thumbnail = { url: '', height: 'MEDIUM' };
        }
      }
      
      // IMPORTANT: Get suggestions from message.suggestions (not from richCard)
      // Suggestions are always stored at message level
      if (message.suggestions && message.suggestions.length > 0) {
        this.richCard.suggestions = [...message.suggestions];
      } else if (!this.richCard.suggestions) {
        this.richCard.suggestions = [];
      }
      
      if (message.fallbackText) {
        this.fallbackText = message.fallbackText;
      }
      
      if (message.fallbackSenderId) {
        this.selectedSenderId = parseInt(message.fallbackSenderId) || 0;
      }
    }
  }

  ngOnDestroy(): void {
    this._subscriptionListFolders?.unsubscribe();
    this._subscriptionLists?.unsubscribe();
    this._subscriptionListFields?.unsubscribe();
    this.resetForm();
  }

  onFieldChange(): void {
    this.updateMessage();
  }

  populateSendersDropdown(): void {
    this.smsMessageService.getClientInboxes().subscribe((executionResultDto: ExecutionResultDto) => {
      this._showSpinner = true;

      if (executionResultDto.executionResult === ExecutionResult.success) {
        const clientInboxDtos = executionResultDto.data
          .map((clientInboxDto: ClientInboxDto) => this.clientInboxDtoAdapter.adapt(clientInboxDto));

        this._clientInboxSelectListOptions = clientInboxDtos.map((clientInboxDto: ClientInboxDto) =>
          new SelectListOption(clientInboxDto.id, `(${clientInboxDto.InboxNumber}) ${clientInboxDto.InboxName}`));
      } else {
        this.notificationService.showError(executionResultDto.message);
      }
      this._showSpinner = false;
    }, error => this._showSpinner = false);
  }

  validateField(field: string, value: any): void {
    this.fieldErrors[field] = '';

    if (field === 'title' && value) {
      const profanityCheck = this.validationService.validateTextForProfanity(value);
      if (!profanityCheck.valid) {
        this.fieldErrors['title'] = profanityCheck.error || '';
      }
    } else if (field === 'description' && value) {
      const profanityCheck = this.validationService.validateTextForProfanity(value);
      if (!profanityCheck.valid) {
        this.fieldErrors['description'] = profanityCheck.error || '';
      }
    } else if (field === 'mediaUrl' && value) {
      const urlCheck = this.validationService.validateUrl(value);
      if (!urlCheck.valid) {
        this.fieldErrors['mediaUrl'] = urlCheck.error || '';
      }
    } else if (field === 'thumbnailUrl' && value) {
      const urlCheck = this.validationService.validateUrl(value);
      if (!urlCheck.valid) {
        this.fieldErrors['thumbnailUrl'] = urlCheck.error || '';
      }
    }
  }

  onMediaUrlChange(): void {
    if (!this.richCard.media) {
      this.richCard.media = { url: '', height: 'MEDIUM' };
    }
    this.validateField('mediaUrl', this.richCard.media.url);
    this.updateMessage();
  }

  onThumbnailToggle(): void {
    if (this.hasThumbnail && !this.richCard.thumbnail) {
      this.richCard.thumbnail = { url: '', height: 'MEDIUM' };
    } else if (!this.hasThumbnail) {
      this.richCard.thumbnail = undefined;
      this.fieldErrors['thumbnailUrl'] = '';
    }
    this.updateMessage();
  }

  onThumbnailUrlChange(): void {
    if (this.hasThumbnail && !this.richCard.thumbnail) {
      this.richCard.thumbnail = { url: '', height: 'MEDIUM' };
    }
    this.validateField('thumbnailUrl', this.richCard.thumbnail?.url);
    this.updateMessage();
  }

  openMediaServerModal(target: 'media' | 'thumbnail' = 'media'): void {
    this._mediaServerTarget = target;
    this._showMediaServer = true;
    this.mediaServerService.selectedFiles = [];
    this.mediaServerModal.show();
  }

  applyMediaFromServer(): void {
    const files: S3ObjectsModel[] = this.mediaServerService.selectedFiles;

    if (!files || files.length === 0) {
      this.notificationService.showWarning('Please select a file to insert.');
      return;
    }

    const selectedFile = files[0];
    const publicUrl = this.mediaServerService.getFilePublicUrl(selectedFile);

    if (!publicUrl || publicUrl === 'N/A') {
      this.notificationService.showWarning('Selected file type is not supported for RCS rich cards.');
      return;
    }

    if (this._mediaServerTarget === 'media') {
      if (!this.richCard.media) {
        this.richCard.media = { url: '', height: 'MEDIUM' };
      }
      this.richCard.media.url = publicUrl;
      this.onMediaUrlChange();
    } else {
      if (!this.hasThumbnail) {
        this.hasThumbnail = true;
      }
      if (!this.richCard.thumbnail) {
        this.richCard.thumbnail = { url: '', height: 'MEDIUM' };
      }
      this.richCard.thumbnail.url = publicUrl;
      this.onThumbnailUrlChange();
    }

    this.closeMediaServer();
  }

  closeMediaServer(): void {
    this._showMediaServer = false;
    this.mediaServerService.selectedFiles = [];
    this._mediaServerTarget = 'media';
    this.mediaServerModal.hide();
  }

  onMediaLoadError(field: 'mediaUrl' | 'thumbnailUrl' = 'mediaUrl'): void {
    // Handle image load errors
    this.fieldErrors[field] = 'Failed to load image from URL';
  }

  onFallbackTextChange(): void {
    this.validateFallbackText();
    this.updateMessage();
  }

  onSenderChange(): void {
    if (this.selectedSenderId === 0) {
      this.senderError = 'Please select a sender';
    } else {
      this.senderError = '';
    }
    this.updateMessage();
  }

  addSuggestion(): void {
    this.suggestionErrors = {};

    const displayTextCheck = this.validationService.validateRequired(
      this.newSuggestion.displayText,
      'Display Text'
    );
    if (!displayTextCheck.valid) {
      this.suggestionErrors['displayText'] = displayTextCheck.error || '';
      return;
    }

    const profanityCheck = this.validationService.validateTextForProfanity(this.newSuggestion.displayText);
    if (!profanityCheck.valid) {
      this.suggestionErrors['displayText'] = profanityCheck.error || '';
      return;
    }

    if (this.newSuggestion.type === SuggestionType.OPEN_URL) {
      if (this.newSuggestion.url) {
        const urlCheck = this.validationService.validateUrl(this.newSuggestion.url);
        if (!urlCheck.valid) {
          this.suggestionErrors['url'] = urlCheck.error || '';
          return;
        }
      }
    }

    if (this.newSuggestion.type === SuggestionType.DIAL_PHONE) {
      if (this.newSuggestion.phone) {
        const phoneCheck = this.validationService.validatePhone(this.newSuggestion.phone);
        if (!phoneCheck.valid) {
          this.suggestionErrors['phone'] = phoneCheck.error || '';
          return;
        }
      }
    }

    if (this.newSuggestion.type === SuggestionType.VIEW_LOCATION) {
      if (this.newSuggestion.latitude !== undefined) {
        const latCheck = this.validationService.validateLatitude(this.newSuggestion.latitude);
        if (!latCheck.valid) {
          this.suggestionErrors['latitude'] = latCheck.error || '';
          return;
        }
      }
      if (this.newSuggestion.longitude !== undefined) {
        const lngCheck = this.validationService.validateLongitude(this.newSuggestion.longitude);
        if (!lngCheck.valid) {
          this.suggestionErrors['longitude'] = lngCheck.error || '';
          return;
        }
      }
    }

    if (this.newSuggestion.type === SuggestionType.CALENDAR_EVENT) {
      if (this.newSuggestion.meetingTitle) {
        const titleCheck = this.validationService.validateTextForProfanity(this.newSuggestion.meetingTitle);
        if (!titleCheck.valid) {
          this.suggestionErrors['meetingTitle'] = titleCheck.error || '';
          return;
        }
      }
      if (this.newSuggestion.meetingDescription) {
        const descCheck = this.validationService.validateTextForProfanity(this.newSuggestion.meetingDescription);
        if (!descCheck.valid) {
          this.suggestionErrors['meetingDescription'] = descCheck.error || '';
          return;
        }
      }
      if (this.newSuggestion.startTime) {
        const startCheck = this.validationService.validateDateTime(this.newSuggestion.startTime);
        if (!startCheck.valid) {
          this.suggestionErrors['startTime'] = startCheck.error || '';
          return;
        }
      }
      if (this.newSuggestion.endTime) {
        const endCheck = this.validationService.validateDateTime(this.newSuggestion.endTime);
        if (!endCheck.valid) {
          this.suggestionErrors['endTime'] = endCheck.error || '';
          return;
        }
      }
      if (this.newSuggestion.startTime && this.newSuggestion.endTime) {
        const startDate = new Date(this.newSuggestion.startTime);
        const endDate = new Date(this.newSuggestion.endTime);
        if (endDate <= startDate) {
          this.suggestionErrors['endTime'] = 'End time must be after start time';
          return;
        }
      }
    }

    this.richCard.suggestions = this.richCard.suggestions || [];
    this.richCard.suggestions.push({ ...this.newSuggestion });
    this.resetNewSuggestion();
    this.updateMessage();
  }

  removeSuggestion(index: number): void {
    if (this.richCard.suggestions) {
      this.richCard.suggestions.splice(index, 1);
      this.updateMessage();
    }
  }

  editSuggestion(index: number): void {
    if (this.richCard.suggestions && this.richCard.suggestions[index]) {
      this.editingIndex = index;
      this.editingSuggestion = { ...this.richCard.suggestions[index] };
      this.editingSuggestionErrors = {};
    }
  }

  saveEditingSuggestion(): void {
    this.editingSuggestionErrors = {};

    // Validate display text
    const displayTextCheck = this.validationService.validateRequired(
      this.editingSuggestion.displayText,
      'Display Text'
    );
    if (!displayTextCheck.valid) {
      this.editingSuggestionErrors['displayText'] = displayTextCheck.error || '';
      return;
    }

    const profanityCheck = this.validationService.validateTextForProfanity(this.editingSuggestion.displayText);
    if (!profanityCheck.valid) {
      this.editingSuggestionErrors['displayText'] = profanityCheck.error || '';
      return;
    }

    // Validate type-specific fields
    if (this.editingSuggestion.type === SuggestionType.OPEN_URL && this.editingSuggestion.url) {
      const urlCheck = this.validationService.validateUrl(this.editingSuggestion.url);
      if (!urlCheck.valid) {
        this.editingSuggestionErrors['url'] = urlCheck.error || '';
        return;
      }
    }

    if (this.editingSuggestion.type === SuggestionType.DIAL_PHONE && this.editingSuggestion.phone) {
      const phoneCheck = this.validationService.validatePhone(this.editingSuggestion.phone);
      if (!phoneCheck.valid) {
        this.editingSuggestionErrors['phone'] = phoneCheck.error || '';
        return;
      }
    }

    if (this.editingSuggestion.type === SuggestionType.VIEW_LOCATION) {
      if (this.editingSuggestion.latitude !== undefined) {
        const latCheck = this.validationService.validateLatitude(this.editingSuggestion.latitude);
        if (!latCheck.valid) {
          this.editingSuggestionErrors['latitude'] = latCheck.error || '';
          return;
        }
      }
      if (this.editingSuggestion.longitude !== undefined) {
        const lngCheck = this.validationService.validateLongitude(this.editingSuggestion.longitude);
        if (!lngCheck.valid) {
          this.editingSuggestionErrors['longitude'] = lngCheck.error || '';
          return;
        }
      }
    }

    // Update the suggestion
    if (this.richCard.suggestions) {
      this.richCard.suggestions[this.editingIndex] = { ...this.editingSuggestion };
    }
    this.cancelEditingSuggestion();
    this.updateMessage();
  }

  cancelEditingSuggestion(): void {
    this.editingIndex = -1;
    this.editingSuggestion = {
      type: SuggestionType.REPLY,
      displayText: ''
    };
    this.editingSuggestionErrors = {};
  }

  onEditingSuggestionTypeChange(): void {
    // Reset type-specific fields when type changes
    this.editingSuggestion.url = undefined;
    this.editingSuggestion.phone = undefined;
    this.editingSuggestion.address = undefined;
    this.editingSuggestion.latitude = undefined;
    this.editingSuggestion.longitude = undefined;
    this.editingSuggestion.meetingTitle = undefined;
    this.editingSuggestion.meetingDescription = undefined;
    this.editingSuggestion.startTime = undefined;
    this.editingSuggestion.endTime = undefined;
    this.editingSuggestionErrors = {};
  }

  onSuggestionTypeChange(): void {
    this.newSuggestion.url = undefined;
    this.newSuggestion.phone = undefined;
    this.newSuggestion.address = undefined;
    this.newSuggestion.latitude = undefined;
    this.newSuggestion.longitude = undefined;
    this.newSuggestion.startTime = undefined;
    this.newSuggestion.endTime = undefined;
    this.newSuggestion.meetingTitle = undefined;
    this.newSuggestion.meetingDescription = undefined;
    this.suggestionErrors = {};
  }

  initWildcardForm(): void {
    this._formGroupInsertWildcard = new UntypedFormGroup({
      listFolders: new UntypedFormControl({ value: 0, disabled: true }, Validators.required),
      lists: new UntypedFormControl({ value: 0, disabled: true }, Validators.required),
      listFields: new UntypedFormControl({ value: 0, disabled: true }, Validators.required)
    });
  }

  rememberSelection(event: any, target: 'title' | 'description' | 'fallback'): void {
    if (event?.target?.selectionStart !== undefined) {
      this._selectionStart = event.target.selectionStart;
    }
    this._wildcardTarget = target;
  }

  openWildcard(target: 'title' | 'description' | 'fallback', inputEl: HTMLInputElement | HTMLTextAreaElement): void {
    this._wildcardTarget = target;
    this._selectionStart = inputEl?.selectionStart || 0;
    this._formGroupInsertWildcard.reset({ listFolders: 0, lists: 0, listFields: 0 });
    this.insertWildcardModal.show();
  }

  insertWildcardModalSubmit(): void {
    const field = this._listFieldsSelectListOptions.find(x => x.value === this._selectedListFieldId)?.simpleName || '';
    const targetValue = this._wildcardTarget === 'title'
      ? this.richCard.title
      : this._wildcardTarget === 'description'
        ? this.richCard.description
        : this.fallbackText;
    const before = targetValue.substring(0, this._selectionStart);
    const after = targetValue.substring(this._selectionStart);
    const withWildcard = `${before}${field}${after}`;

    if (this._wildcardTarget === 'title') {
      this.richCard.title = withWildcard;
      this.onFieldChange();
    } else if (this._wildcardTarget === 'description') {
      this.richCard.description = withWildcard;
      this.onFieldChange();
    } else {
      this.fallbackText = withWildcard;
      this.onFallbackTextChange();
    }

    this.insertWildcardModal.hide();
  }

  subscribeToWildcardFormControls(): void {
    this._subscriptionListFolders = this._formGroupInsertWildcard.get('listFolders')?.valueChanges.subscribe(folderId => {
      this._selectedListFolderId = folderId;
      this.populateListsDropdown();
    });
    this._subscriptionLists = this._formGroupInsertWildcard.get('lists')?.valueChanges.subscribe(listId => {
      this._selectedListId = listId;
      this.populateListFieldsDropdown();
    });
    this._subscriptionListFields = this._formGroupInsertWildcard.get('listFields')?.valueChanges.subscribe(listFieldId => {
      this._selectedListFieldId = listFieldId;
    });
  }

  populateListFoldersDropdown(): void {
    this._showSpinner = true;
    this._formGroupInsertWildcard.get('listFolders')?.disable();

    this.listFolderService.getAll().subscribe((executionResultDto: ExecutionResultDto) => {
      if (executionResultDto.executionResult === ExecutionResult.success) {
        const listFolderDtos = executionResultDto.data
          .map((dto: ListFolderDto) => this.listFolderDtoAdapter.adapt(dto));

        this._listFolderSelectListOptions = listFolderDtos.map((dto: ListFolderDto) =>
          new SelectListOption(dto.id, dto.name));
      } else {
        this.notificationService.showError(executionResultDto.message);
      }
      this._showSpinner = false;
      this._formGroupInsertWildcard.get('listFolders')?.enable();
    }, () => {
      this._showSpinner = false;
      this._formGroupInsertWildcard.get('listFolders')?.enable();
    });
  }

  populateListsDropdown(): void {
    if (!this._selectedListFolderId) { return; }

    this._showSpinner = true;
    this._formGroupInsertWildcard.get('lists')?.disable();

    this.listsService.getByFolderId(this._selectedListFolderId).subscribe((executionResultDto: ExecutionResultDto) => {
      if (executionResultDto.executionResult === ExecutionResult.success) {
        const listDtos = executionResultDto.data
          .map((dto: ListModel) => this.listModelAdapter.adapt(dto));

        this._listsSelecListOptions = listDtos.map((dto: ListModel) =>
          new SelectListOption(dto.ListID, dto.ListName));

        this._selectedListId = this._listsSelecListOptions.length === 1 ? this._listsSelecListOptions[0].value : 0;
        this._formGroupInsertWildcard.get('lists')?.setValue(this._selectedListId);
      } else {
        this.notificationService.showError(executionResultDto.message);
      }
      this._showSpinner = false;
      this._formGroupInsertWildcard.get('lists')?.enable();
    }, () => {
      this._showSpinner = false;
      this._formGroupInsertWildcard.get('lists')?.enable();
    });
  }

  populateListFieldsDropdown(): void {
    if (!this._selectedListId) { return; }

    this._showSpinner = true;
    this._formGroupInsertWildcard.get('listFields')?.disable();

    this.listsService.getWildcardListFields(this._selectedListId).subscribe((executionResultDto: ExecutionResultDto) => {
      if (executionResultDto.executionResult === ExecutionResult.success) {
        const listFieldDtos = executionResultDto.data
          .map((dto: ListFieldsModel) => this.listFieldsModelAdapter.adapt(dto));

        this._listFieldsSelectListOptions = listFieldDtos.map((dto: ListFieldsModel) => {
          const label = dto.FieldName || dto.DisplayName;
          return new SelectListOption(dto.FieldID, label, dto.Wildcard);
        });

        this._selectedListFieldId = this._listFieldsSelectListOptions.length === 1
          ? this._listFieldsSelectListOptions[0].value
          : 0;

        this._formGroupInsertWildcard.get('listFields')?.setValue(this._selectedListFieldId);
      } else {
        this.notificationService.showError(executionResultDto.message);
      }
      this._showSpinner = false;
      this._formGroupInsertWildcard.get('listFields')?.enable();
    }, () => {
      this._showSpinner = false;
      this._formGroupInsertWildcard.get('listFields')?.enable();
    });
  }

  validateSuggestionField(field: string, value: any): void {
    this.suggestionErrors[field] = '';
    
    if (field === 'url' && value) {
      const check = this.validationService.validateUrl(value);
      if (!check.valid) {
        this.suggestionErrors['url'] = check.error || '';
      }
    } else if (field === 'phone' && value) {
      const check = this.validationService.validatePhone(value);
      if (!check.valid) {
        this.suggestionErrors['phone'] = check.error || '';
      }
    } else if (field === 'latitude' && value !== undefined && value !== null && value !== '') {
      const check = this.validationService.validateLatitude(Number(value));
      if (!check.valid) {
        this.suggestionErrors['latitude'] = check.error || '';
      }
    } else if (field === 'longitude' && value !== undefined && value !== null && value !== '') {
      const check = this.validationService.validateLongitude(Number(value));
      if (!check.valid) {
        this.suggestionErrors['longitude'] = check.error || '';
      }
    } else if (field === 'displayText' && value) {
      const profanityCheck = this.validationService.validateTextForProfanity(value);
      if (!profanityCheck.valid) {
        this.suggestionErrors['displayText'] = profanityCheck.error || '';
      }
    } else if (field === 'meetingTitle' && value) {
      const profanityCheck = this.validationService.validateTextForProfanity(value);
      if (!profanityCheck.valid) {
        this.suggestionErrors['meetingTitle'] = profanityCheck.error || '';
      }
    } else if (field === 'meetingDescription' && value) {
      const profanityCheck = this.validationService.validateTextForProfanity(value);
      if (!profanityCheck.valid) {
        this.suggestionErrors['meetingDescription'] = profanityCheck.error || '';
      }
    }
  }

  resetNewSuggestion(): void {
    this.newSuggestion = {
      type: SuggestionType.REPLY,
      displayText: ''
    };
    this.suggestionErrors = {};
  }

  updateMessage(): void {
    const updatedMessage: RcsMessage = {
      type: MessageType.STANDALONE,
      richCard: {
        title: this.richCard.title,
        description: this.richCard.description,
        orientation: this.richCard.orientation,
        thumbnailAlignment: this.richCard.thumbnailAlignment,
        media: this.richCard.media,
        thumbnail: this.richCard.thumbnail
      },
      suggestions: this.richCard.suggestions && this.richCard.suggestions.length > 0 ? [...this.richCard.suggestions] : undefined,
      fallbackText: this.fallbackText,
      fallbackSenderId: this.selectedSenderId ? this.selectedSenderId.toString() : undefined,
      timestamp: new Date()
    };
    this.messageChange.emit(updatedMessage);
  }

  save(messageId: number = 0, messageName: string = '', folderId: number = 0): Promise<number> {
    return new Promise((resolve, reject) => {
      this.validateAll();
      
      if (this.hasValidationErrors()) {
        this.notificationService.showError('Please fix validation errors before saving');
        reject(new Error('Validation errors'));
        return;
      }

    const rcsContentJson = JSON.stringify({
      type: 'richcard',
      richCard: {
        title: this.richCard.title,
        description: this.richCard.description,
        orientation: this.richCard.orientation,
        thumbnailAlignment: this.richCard.thumbnailAlignment,
        media: this.richCard.media,
        thumbnail: this.richCard.thumbnail
      }
    });

    const rcsMessageDtoRequest = new RcsMessageDtoRequest({
      id: messageId,
      folderId: folderId,
      name: messageName,
      rcsType: 'richcard',
      rcsContentJson: rcsContentJson,
      suggestions: this.richCard.suggestions.map(s => ({
        type: s.type,
        displayText: s.displayText,
        suggestionScope: 'global',
        url: s.url,
        phone: s.phone,
        address: s.address,
        latitude: s.latitude,
        longitude: s.longitude,
        startTime: s.startTime,
        endTime: s.endTime,
        meetingTitle: s.meetingTitle,
        meetingDescription: s.meetingDescription
      })),
      smsFallbackEnabled: true,
      smsFallbackText: this.fallbackText,
      smsFallbackClientInboxId: this.selectedSenderId,
    });

    this._showSpinner = true;

      this.rcsMessageService.save(rcsMessageDtoRequest).subscribe((executionResultDto: ExecutionResultDto) => {
        if (executionResultDto.executionResult === ExecutionResult.success) {
          const rcsMessageDto = this.rcsMessageDtoAdapter.adapt(executionResultDto.data);
          this.notificationService.showSuccess('RCS Rich Card saved successfully');
          this.messageChange.emit({
            ...this.message,
            richCard: this.richCard
          });
          resolve(rcsMessageDto.messageId || messageId);
        } else {
          this.notificationService.showError(executionResultDto.message);
          reject(new Error(executionResultDto.message));
        }
        this._showSpinner = false;
      }, error => {
        this.notificationService.showError('Error saving RCS Rich Card');
        this._showSpinner = false;
        reject(error);
      });
    });
  }

  private hasValidationErrors(): boolean {
    return !!(Object.keys(this.fieldErrors).length > 0 ||
      Object.keys(this.suggestionErrors).length > 0 ||
      this.fallbackTextError || this.senderError);
  }

  private validateAll(): void {
    this.fieldErrors = {};
    this.suggestionErrors = {};
    this.fallbackTextError = '';
    this.senderError = '';

    // Validate title
    if (!this.richCard.title?.trim()) {
      this.fieldErrors['title'] = 'Title is required';
    }

    // Validate description  
    if (!this.richCard.description?.trim()) {
      this.fieldErrors['description'] = 'Description is required';
    }

    // Validate media URL
    if (!this.richCard.media?.url?.trim()) {
      this.fieldErrors['mediaUrl'] = 'Media URL is required';
    }

    // Validate fallback text and sender
    this.validateFallbackText();
    this.validateSender();
  }

  validateFallbackText(): void {
    this.fallbackTextError = '';
    if (!this.fallbackText || this.fallbackText.trim() === '') {
      this.fallbackTextError = 'Fallback text is required';
      return;
    }
    const profanityCheck = this.validationService.validateTextForProfanity(this.fallbackText);
    if (!profanityCheck.valid) {
      this.fallbackTextError = profanityCheck.error || '';
    }
  }

  private validateSender(): void {
    this.senderError = '';
    if (!this.selectedSenderId || this.selectedSenderId === 0) {
      this.senderError = 'Please select a sender';
    }
  }

  load(messageId: number): void {
    if (messageId <= 0) {
      return;
    }

    this._showSpinner = true;
    this.rcsMessageService.get(messageId).subscribe((executionResultDto: ExecutionResultDto) => {
      if (executionResultDto.executionResult === ExecutionResult.success) {
        const rcsMessageDto = this.rcsMessageDtoAdapter.adapt(executionResultDto.data);
        this.loadFromRcsMessage(rcsMessageDto);
      } else {
        this.notificationService.showError(executionResultDto.message || 'Failed to load RCS message');
      }
      this._showSpinner = false;
    }, error => {
      this.notificationService.showError('Error loading RCS message');
      this._showSpinner = false;
      console.error('Error loading RCS message:', error);
    });
  }

  private loadFromRcsMessage(rcsMessageDto: any): void {
    try {
      const contentJsonStr = rcsMessageDto.rcsContentJson || rcsMessageDto.RCSContentJson;
      const rcsContent = JSON.parse(contentJsonStr);
      const suggestionsFromDto = (rcsMessageDto.Suggestions || rcsMessageDto.suggestions || []) as any[];
      
      if (rcsContent.type === 'richcard') {
        const card = rcsContent.richCard || rcsContent.RichCard || {};
        this.richCard = {
          title: card.title || card.Title || '',
          description: card.description || card.Description || '',
          orientation: card.orientation || card.Orientation || 'VERTICAL',
          thumbnailAlignment: card.thumbnailAlignment || card.ThumbnailAlignment || 'LEFT',
          media: card.media || card.Media || { url: '', height: 'MEDIUM' },
          thumbnail: card.thumbnail || card.Thumbnail,
          suggestions: []
        };
      }

      // Suggestions arrive separately from backend; map them into the rich card
      this.richCard.suggestions = suggestionsFromDto
        .filter(s => (s.SuggestionScope || s.suggestionScope || '').toLowerCase() === 'global')
        .map(s => ({
          type: (s.Type || s.type || '').toString() as any,
          displayText: s.DisplayText || s.displayText || '',
          url: s.Url || s.url,
          phone: s.Phone || s.phone,
          address: s.Address || s.address,
          latitude: s.Latitude ?? s.latitude,
          longitude: s.Longitude ?? s.longitude,
          startTime: s.StartTime || s.startTime,
          endTime: s.EndTime || s.endTime,
          meetingTitle: s.MeetingTitle || s.meetingTitle,
          meetingDescription: s.MeetingDescription || s.meetingDescription
        }));
      
      this.fallbackText = rcsMessageDto.smsFallbackText || '';
      this.selectedSenderId = rcsMessageDto.smsFallbackClientInboxId || 0;
      
      this.updateMessage();
    } catch (error) {
      this.notificationService.showError('Invalid message format');
      console.error('Load error', error);
    }
  }

  resetForm(): void {
    // Reset rich card data
    this.richCard = {
      orientation: 'VERTICAL',
      title: '',
      description: '',
      media: {
        url: '',
        height: 'MEDIUM'
      },
      suggestions: []
    };
    
    // Reset fallback text
    this.fallbackText = '';
    this.selectedSenderId = 0;
    
    // Clear errors
    this.fieldErrors = {};
    this.suggestionErrors = {};
    this.fallbackTextError = '';
    this.senderError = '';
    
    // Reset message
    this.message = {
      type: MessageType.STANDALONE,
      richCard: this.richCard,
      fallbackText: '',
      fallbackSenderId: undefined
    };
    
    // Emit reset message to parent
    this.messageChange.emit(this.message);
    this.updateMessage();
  }
}
